/** Files attached to business records: list, counts for list screens, upload, rename, remove. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, fetchBlob, unwrap, type Schemas } from '../../api/client';

export type Attachment = Schemas['Attachment'];
export type RecordType = Schemas['AttachmentRecordType'];

export const attachmentKeys = {
  all: ['attachments'] as const,
  list: (type: RecordType, id: string) => ['attachments', 'list', type, id] as const,
  counts: (type: RecordType, ids: string[]) => ['attachments', 'counts', type, ids] as const,
  file: (id: string) => ['attachments', 'file', id] as const,
};

export function useAttachments(type: RecordType, id: string | undefined) {
  return useQuery({
    queryKey: attachmentKeys.list(type, id ?? ''),
    queryFn: () =>
      unwrap(
        api.GET('/attachments', { params: { query: { recordType: type, recordId: id ?? '' } } }),
      ),
    enabled: !!id,
    retry: false,
  });
}

/** Paper-clip counts for the rows of a list (records without files are left out). */
export function useAttachmentCounts(type: RecordType, ids: string[]): Record<string, number> {
  const sorted = [...new Set(ids)].sort().slice(0, 200);
  const counts = useQuery({
    queryKey: attachmentKeys.counts(type, sorted),
    queryFn: () =>
      unwrap(
        api.GET('/attachments/counts', {
          params: { query: { recordType: type, ids: sorted.join(',') } },
        }),
      ),
    enabled: sorted.length > 0,
    staleTime: 30_000,
    retry: false,
  });
  return counts.data?.counts ?? {};
}

/** The file's bytes as an object URL (kept while the query lives). */
export function useAttachmentFile(file: Attachment | null, enabled = true) {
  return useQuery({
    queryKey: attachmentKeys.file(file?.id ?? ''),
    queryFn: async () => URL.createObjectURL(await fetchBlob(file?.url ?? '')),
    enabled: !!file && enabled,
    staleTime: Infinity,
    gcTime: 5 * 60_000,
    retry: false,
  });
}

/** Saves the file under its own name. */
export async function downloadAttachment(file: Attachment) {
  const href = URL.createObjectURL(await fetchBlob(file.url));
  const a = document.createElement('a');
  a.href = href;
  a.download = file.fileName;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

export function useAttachmentActions(type: RecordType, id: string) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: attachmentKeys.all });
  return {
    upload: useMutation({
      mutationFn: (input: { file: File; description?: string }) =>
        unwrap(
          api.POST('/attachments', {
            params: {
              query: {
                recordType: type,
                recordId: id,
                fileName: input.file.name,
                ...(input.description ? { description: input.description } : {}),
              },
            },
            // The API takes the raw file bytes; it checks the type itself.
            body: input.file as unknown as string,
            bodySerializer: (b: unknown) => b as BodyInit,
            headers: { 'Content-Type': 'application/octet-stream' },
          }),
        ),
      onSettled: refresh,
    }),
    describe: useMutation({
      mutationFn: (input: { file: Attachment; description: string }) =>
        unwrap(
          api.PATCH('/attachments/{id}', {
            params: { path: { id: input.file.id } },
            body: { description: input.description.trim() || null, version: input.file.version },
          }),
        ),
      onSettled: refresh,
    }),
    remove: useMutation({
      mutationFn: (file: Attachment) =>
        unwrap(api.DELETE('/attachments/{id}', { params: { path: { id: file.id } } })),
      onSettled: refresh,
    }),
  };
}
